import React, { useState } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    propertyType: '',
    projectStart: '',
    message: ''
  });

  const whatsappNumber = '919910837533';
  const whatsappMessage = 'Hi, I would like to discuss my interior design project.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Static HTML version - Create mailto link with form data
    const subject = encodeURIComponent(`Interior Design Inquiry - ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Phone: ${formData.phone}\n` +
      `Email: ${formData.email}\n` +
      `Location: ${formData.location}\n` +
      `Property Type: ${formData.propertyType}\n` +
      `Project Start: ${formData.projectStart}\n\n` +
      `Message:\n${formData.message}`
    );
    
    // Store in localStorage for reference
    const existingSubmissions = JSON.parse(localStorage.getItem('mayoDesignSubmissions') || '[]');
    const newSubmission = {
      ...formData,
      id: Date.now(),
      submittedAt: new Date().toISOString()
    };
    existingSubmissions.push(newSubmission);
    localStorage.setItem('mayoDesignSubmissions', JSON.stringify(existingSubmissions));
    
    // Open email client
    window.location.href = `mailto:info@mayodesign.in?subject=${subject}&body=${body}`;
    
    toast({
      title: "Opening Email Client",
      description: "Please send the email to complete your inquiry. We'll contact you within 24 hours.",
    });
    
    // Reset form after short delay
    setTimeout(() => {
      setFormData({
        name: '',
        phone: '',
        email: '',
        location: '',
        propertyType: '',
        projectStart: '',
        message: ''
      });
    }, 1000);
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-6 font-poppins">
              Let's Create Your Dream Home
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Reach out to discuss your project, schedule a site visit, or ask any questions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#2F4538] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2 font-poppins">Call Us</h3>
                <a href="tel:+919910837533" className="text-[#2F4538] hover:underline">
                  +91 99108 37533
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#2F4538] rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2 font-poppins">WhatsApp</h3>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2F4538] hover:underline"
                >
                  Message Us
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#2F4538] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2 font-poppins">Email</h3>
                <a href="mailto:info@mayodesign.in" className="text-[#2F4538] hover:underline">
                  info@mayodesign.in
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-[#E8DCC4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#2C2C2C] mb-6 font-poppins">
                Share Your Project Details
              </h2>
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                      <Input
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="Your phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                      <Input
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="Project location"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                      <Select value={formData.propertyType} onValueChange={(value) => handleChange('propertyType', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="builder-floor">Builder Floor</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Approximate Project Start</label>
                      <Input
                        value={formData.projectStart}
                        onChange={(e) => handleChange('projectStart', e.target.value)}
                        placeholder="e.g., Next month, 3 months"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Tell us about your project requirements..."
                        rows={4}
                      />
                    </div>

                    <Button type="submit" className="w-full bg-[#2F4538] hover:bg-[#243629] text-white transition-all">
                      <Send className="mr-2 w-5 h-5" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Location Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#2C2C2C] mb-6 font-poppins">
                Visit Our Studio
              </h2>
              <Card className="border-none shadow-lg mb-6">
                <CardContent className="p-8">
                  <div className="flex items-start mb-6">
                    <MapPin className="w-6 h-6 text-[#2F4538] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#2C2C2C] mb-2">Mayo Design</h3>
                      <p className="text-gray-700 leading-relaxed">
                        86, Sector 86 Rd, Sector 86<br />
                        Meoka, Gurugram<br />
                        Haryana 122505
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-[#2C2C2C] mb-3">Office Hours</h3>
                    <p className="text-gray-700">
                      Monday - Saturday: 10:00 AM - 7:00 PM<br />
                      Sunday: By Appointment
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Map Placeholder */}
              <div className="bg-gray-300 rounded-lg shadow-lg h-64 flex items-center justify-center">
                <p className="text-gray-600">Map Location: CW2M+6P, Gurugram</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;